import {
	BellOutlined,
	CheckCircleOutlined,
	CheckOutlined,
	DeleteOutlined,
	ExclamationCircleOutlined,
	InfoCircleOutlined,
	MoreOutlined,
	SettingOutlined,
	SyncOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import {
	Avatar,
	Badge,
	Button,
	Drawer,
	Dropdown,
	Empty,
	List,
	Popconfirm,
	Space,
	Tabs,
	Tooltip,
	Typography,
} from "antd";
import { FadeIn } from "components/polish";
import { Chip } from "components/ui";
import { format, formatDistanceToNow } from "date-fns";
import type { CSSProperties } from "react";
import { useRef, useState } from "react";
import type {
	Notification,
	NotificationCenterProps,
} from "../../types/notification.types";
import { DATEFNS_DATE_FORMAT } from "../../utils/dates";
import { groupNotificationsByTime } from "../../utils/notificationBuckets";

const { Text, Paragraph } = Typography;

export function NotificationCenter({
	notifications = [],
	onRefresh,
	onMarkAsRead,
	onMarkAllAsRead,
	onDelete,
	onDeleteAll,
	onSettingsClick,
	onView,
	unreadCount: serverUnreadCount,
	hasMore = false,
	onLoadMore,
}: NotificationCenterProps) {
	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("all");
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	// Remember what was unread when the drawer opened, so this viewing still
	// shows which items are new after mark-on-open clears them. Cleared on close.
	const [seenOnOpen, setSeenOnOpen] = useState<ReadonlySet<string>>(
		() => new Set(),
	);
	const loadMoreInFlight = useRef(false);

	const showDrawer = () => {
		setSeenOnOpen(
			new Set(notifications.filter((n) => !n.read).map((n) => n.id)),
		);
		setOpen(true);
		// Opening the center counts as reading: clear unread immediately so the
		// bell badge clears and the next open shows everything as read.
		if (notifications.some((n) => !n.read)) onMarkAllAsRead?.();
	};
	const closeDrawer = () => {
		setOpen(false);
		setSeenOnOpen(new Set());
	};
	const isNew = (n: Notification) => !n.read || seenOnOpen.has(n.id);

	const handleLoadMore = async () => {
		if (!onLoadMore || loadMoreInFlight.current) return;
		loadMoreInFlight.current = true;
		setLoadingMore(true);
		try {
			await onLoadMore();
		} finally {
			loadMoreInFlight.current = false;
			setLoadingMore(false);
		}
	};

	const handleMarkAsRead = (id: string) => {
		onMarkAsRead?.(id);
	};

	const handleMarkAllAsRead = () => {
		onMarkAllAsRead?.();
	};

	const handleDelete = (id: string) => {
		onDelete?.(id);
	};

	const handleDeleteAll = () => {
		onDeleteAll?.();
	};

	const handleRefresh = async () => {
		if (!onRefresh) return;
		setLoading(true);
		try {
			await onRefresh();
		} finally {
			setLoading(false);
		}
	};

	const handleSettingsClick = () => {
		onSettingsClick?.();
	};

	const handleView = (item: Notification) => {
		if (!item.read) handleMarkAsRead(item.id);
		onView?.(item);
		setOpen(false);
	};

	// Action-required items live in their own tab; keep them OUT of Alerts so a
	// single notification never appears under both Alerts and Action.
	const alertCount = notifications.filter(
		(n) => (n.type === "alert" || n.type === "warning") && !n.actionRequired,
	).length;
	const actionRequiredCount = notifications.filter(
		(n) => n.actionRequired,
	).length;

	// Filter notifications based on active tab
	const filteredNotifications = notifications.filter((notification) => {
		if (activeTab === "all") return true;
		if (activeTab === "unread") return isNew(notification);
		if (activeTab === "alerts")
			return (
				(notification.type === "alert" || notification.type === "warning") &&
				!notification.actionRequired
			);
		if (activeTab === "actionRequired") return notification.actionRequired;
		return true;
	});

	// The badge shows the server's count: the drawer only holds the loaded
	// pages, so counting them undercounts once more are unread than that.
	const unreadCount =
		serverUnreadCount ?? notifications.filter((n) => !n.read).length;
	const newCount = notifications.filter(isNew).length;

	// Get notification icon
	const getNotificationIcon = (type: string) => {
		switch (type) {
			case "alert":
				return (
					<ExclamationCircleOutlined style={{ color: "var(--color-error)" }} />
				);
			case "warning":
				return (
					<ExclamationCircleOutlined
						style={{ color: "var(--color-warning)" }}
					/>
				);
			case "info":
				return <InfoCircleOutlined style={{ color: "var(--color-info)" }} />;
			case "success":
				return (
					<CheckCircleOutlined style={{ color: "var(--color-success)" }} />
				);
			default:
				return <InfoCircleOutlined style={{ color: "var(--color-info)" }} />;
		}
	};

	// Format notification date
	const formatNotificationDate = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffInHours = Math.abs(now.getTime() - date.getTime()) / 36e5;

		if (diffInHours < 24) {
			return formatDistanceToNow(date, { addSuffix: true });
		}
		return format(date, `${DATEFNS_DATE_FORMAT} h:mm a`);
	};

	const renderItem = (item: Notification) => (
		<List.Item
			className={isNew(item) ? "bg-blue-50" : ""}
			style={{
				padding: "12px 14px",
				marginBottom: 8,
				borderRadius: 8,
				border: "1px solid rgba(0, 0, 0, 0.06)",
			}}
			actions={[
				<Dropdown key="more" menu={itemMenu(item)} trigger={["click"]}>
					<Button type="text" icon={<MoreOutlined />} size="small" />
				</Dropdown>,
			]}
		>
			<List.Item.Meta
				avatar={
					<Avatar
						icon={getNotificationIcon(item.type)}
						style={{ backgroundColor: "transparent" }}
					/>
				}
				title={
					<div>
						<Text
							strong={isNew(item)}
							style={{ wordBreak: "normal", overflowWrap: "break-word" }}
						>
							{item.title}
						</Text>
						<Text type="secondary" className="text-xs notif-date">
							{formatNotificationDate(item.date)}
						</Text>
					</div>
				}
				description={
					<div>
						<Paragraph ellipsis={{ rows: 2 }}>{item.message}</Paragraph>
						<div className="flex items-center justify-between gap-2 mt-1">
							<Space size={4} wrap>
								<Chip variant="blue">{item.source}</Chip>
								{item.actionRequired && (
									<Chip variant="red">Action Required</Chip>
								)}
							</Space>
							{item.link && (
								<Button
									type="link"
									size="small"
									className="p-0"
									onClick={() => handleView(item)}
								>
									View
								</Button>
							)}
						</div>
					</div>
				}
			/>
		</List.Item>
	);

	const visuallyHidden: CSSProperties = {
		position: "absolute",
		width: 1,
		height: 1,
		overflow: "hidden",
		clip: "rect(0 0 0 0)",
		whiteSpace: "nowrap",
	};

	const bucketHeaderStyle: CSSProperties = {
		display: "block",
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		margin: "12px 0 2px",
	};

	const tabItems = [
		{
			key: "all",
			label: <span>All</span>,
		},
		{
			key: "unread",
			label: (
				<span>
					Unread
					{newCount > 0 && (
						<Badge count={newCount} className="notif-tab-badge" />
					)}
				</span>
			),
		},
		{
			key: "alerts",
			label: (
				<span>
					Alerts
					{alertCount > 0 && (
						<Badge count={alertCount} className="notif-tab-badge" />
					)}
				</span>
			),
		},
		{
			key: "actionRequired",
			label: (
				<span>
					Action
					{actionRequiredCount > 0 && (
						<Badge count={actionRequiredCount} className="notif-tab-badge" />
					)}
				</span>
			),
		},
	];

	const itemMenu = (item: Notification): MenuProps => ({
		items: [
			...(!item.read
				? [
						{
							key: "read",
							label: (
								<span>
									<CheckOutlined /> Mark as read
								</span>
							),
							onClick: () => handleMarkAsRead(item.id),
						},
					]
				: []),
			{
				key: "delete",
				label: (
					<span>
						<DeleteOutlined /> Delete
					</span>
				),
				onClick: () => handleDelete(item.id),
			},
		],
	});

	return (
		<>
			<Badge count={unreadCount} size="small" offset={[-4, 6]}>
				<Button
					type="text"
					icon={<BellOutlined style={{ fontSize: 18 }} />}
					onClick={showDrawer}
					aria-label="Notifications"
					style={{ height: 36, width: 36 }}
				/>
			</Badge>

			<Drawer
				title={
					<Space>
						<BellOutlined />
						<span>Notifications</span>
						{unreadCount > 0 && <Badge count={unreadCount} />}
					</Space>
				}
				placement="right"
				onClose={closeDrawer}
				open={open}
				width={400}
				extra={
					<Space size={4}>
						{unreadCount > 0 && (
							<Tooltip title="Mark all as read">
								<Button
									type="text"
									size="small"
									aria-label="Mark all as read"
									icon={<CheckOutlined />}
									onClick={handleMarkAllAsRead}
								/>
							</Tooltip>
						)}
						{notifications.length > 0 && (
							<Popconfirm
								title="Clear all notifications?"
								description="This removes every notification, including older ones not shown here."
								okText="Clear all"
								okButtonProps={{ danger: true }}
								cancelText="Cancel"
								onConfirm={handleDeleteAll}
							>
								{/* No Tooltip here: it opened on top of the confirm. */}
								<Button
									type="text"
									size="small"
									aria-label="Clear all"
									icon={<DeleteOutlined />}
								/>
							</Popconfirm>
						)}
						<Tooltip title="Refresh">
							<Button
								type="text"
								size="small"
								aria-label="Refresh"
								icon={<SyncOutlined spin={loading} />}
								onClick={handleRefresh}
							/>
						</Tooltip>
						<Tooltip title="Settings">
							<Button
								type="text"
								size="small"
								aria-label="Settings"
								icon={<SettingOutlined />}
								onClick={handleSettingsClick}
							/>
						</Tooltip>
					</Space>
				}
			>
				<Tabs
					size="small"
					activeKey={activeTab}
					onChange={setActiveTab}
					items={tabItems}
				/>

				{filteredNotifications.length > 0 ? (
					<FadeIn>
						{groupNotificationsByTime(filteredNotifications).map((bucket) => (
							<div key={bucket.key}>
								<Text type="secondary" style={bucketHeaderStyle}>
									{bucket.label}
								</Text>
								<List
									itemLayout="horizontal"
									split={false}
									dataSource={bucket.items}
									renderItem={renderItem}
								/>
							</div>
						))}
						{/* Inline, not the sr-only utility: the drawer portals outside
						    #root, where Tailwind utilities do not apply. */}
						<div aria-live="polite" style={visuallyHidden}>
							{`${notifications.length} notifications loaded`}
						</div>
						{hasMore && onLoadMore && (
							<div className="notif-load-more">
								<Button onClick={handleLoadMore} loading={loadingMore}>
									Load older
								</Button>
							</div>
						)}
					</FadeIn>
				) : (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={
							<span>
								{activeTab === "all" && "No notifications"}
								{activeTab === "unread" && "No unread notifications left"}
								{activeTab === "alerts" && "No alerts"}
								{activeTab === "actionRequired" &&
									"No action required notifications"}
							</span>
						}
					/>
				)}
			</Drawer>
		</>
	);
}
